import { lazy, ComponentType } from "react";

/**
 * Wraps React.lazy with auto-recovery from stale chunk errors.
 *
 * After a redeploy, the cached index.html may reference hashed JS files
 * that no longer exist ("Failed to fetch dynamically imported module").
 * On first failure we force a one-time hard reload to fetch the new
 * index.html (and the new chunk hashes).
 */
const RELOAD_KEY = "lyko:chunk-reload-attempt";

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      // Success: clear any previous reload flag
      try {
        window.sessionStorage.removeItem(RELOAD_KEY);
      } catch {
        /* ignore */
      }
      return mod;
    } catch (error: any) {
      const message = String(error?.message ?? error);
      const isChunkError =
        message.includes("Failed to fetch dynamically imported module") ||
        message.includes("Importing a module script failed") ||
        message.includes("error loading dynamically imported module");

      if (!isChunkError) throw error;

      let alreadyReloaded = false;
      try {
        alreadyReloaded = window.sessionStorage.getItem(RELOAD_KEY) === "1";
        window.sessionStorage.setItem(RELOAD_KEY, "1");
      } catch {
        /* ignore */
      }

      if (!alreadyReloaded) {
        window.location.reload();
        // Return a never-resolving promise so React keeps the Suspense
        // fallback visible until the page reloads.
        return new Promise<{ default: T }>(() => {});
      }

      throw error;
    }
  });
}
